import React, { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Clock, Tag, CalendarDays, ListOrdered } from "lucide-react";
import Seo, { SITE_URL } from "../Seo"; // WebsiteComponents/blogs/ -> WebsiteComponents/Seo
import { ARTICLES, getArticle, formatDate } from "./articles";

const slugify = (s) =>
  s
    .toLowerCase()
    .replace(/&/g, "and")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");

// Renders **bold** and [text](/internal-path) inside a plain string.
const renderInline = (text) => {
  const parts = String(text).split(/(\*\*[^*]+\*\*|\[[^\]]+\]\([^)]+\))/g);
  return parts.map((part, i) => {
    const bold = part.match(/^\*\*([^*]+)\*\*$/);
    if (bold) return <strong key={i} className="font-semibold text-[#28194f]">{bold[1]}</strong>;
    const link = part.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
    if (link) {
      const [, label, href] = link;
      return href.startsWith("/") ? (
        <Link
          key={i}
          to={href}
          className="font-medium text-[#702cb0] underline decoration-[#702cb0]/30 underline-offset-2 transition-colors hover:text-[#48247f] hover:decoration-[#48247f]"
        >
          {label}
        </Link>
      ) : (
        <a key={i} href={href} target="_blank" rel="noopener noreferrer" className="font-medium text-[#702cb0] underline">
          {label}
        </a>
      );
    }
    return <React.Fragment key={i}>{part}</React.Fragment>;
  });
};

const Paragraphs = ({ items }) =>
  (items || []).map((p, i) => (
    <p key={i} className="mb-5 text-[16px] leading-[1.85] text-slate-700">
      {renderInline(p)}
    </p>
  ));

const Bullets = ({ items }) =>
  items && items.length ? (
    <ul className="mb-6 ml-1 space-y-2.5">
      {items.map((b, i) => (
        <li key={i} className="flex gap-3 text-[16px] leading-[1.75] text-slate-700">
          <span className="mt-[11px] h-1.5 w-1.5 shrink-0 rounded-full bg-[#702cb0]" />
          <span>{renderInline(b)}</span>
        </li>
      ))}
    </ul>
  ) : null;

const BlogPost = () => {
  const { id } = useParams();
  const post = getArticle(id);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [id]);

  if (!post) {
    return (
      <div className="mx-auto max-w-2xl px-4 pb-24 pt-40 text-center">
        <h1 className="text-3xl font-light text-[#28194f]">Article not found</h1>
        <p className="mt-3 text-slate-600">The article you are looking for does not exist or has moved.</p>
        <Link to="/blogs" className="mt-6 inline-flex items-center gap-1 font-semibold text-[#702cb0] hover:text-[#48247f]">
          <ArrowLeft className="h-4 w-4" /> Back to all articles
        </Link>
      </div>
    );
  }

  const path = `/blogs/${post.id}`;
  const url = `${SITE_URL}${path}`;

  const related = [
    ...ARTICLES.filter((a) => a.id !== post.id && a.category === post.category),
    ...ARTICLES.filter((a) => a.id !== post.id && a.category !== post.category),
  ].slice(0, 3);

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: post.title,
      description: post.metaDescription,
      keywords: post.keywords.join(", "),
      articleSection: post.category,
      wordCount: post.wordCount,
      inLanguage: "en-IN",
      datePublished: post.published,
      dateModified: post.modified,
      mainEntityOfPage: { "@type": "WebPage", "@id": url },
      author: { "@type": "Organization", name: "LMSBOTS", url: SITE_URL },
      publisher: { "@id": `${SITE_URL}/#organization` },
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: post.faq.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a.replace(/\*\*/g, "").replace(/\[([^\]]+)\]\([^)]+\)/g, "$1") },
      })),
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: SITE_URL },
        { "@type": "ListItem", position: 2, name: "Blogs", item: `${SITE_URL}/blogs` },
        { "@type": "ListItem", position: 3, name: post.title, item: url },
      ],
    },
  ];

  return (
    <>
      <Seo title={post.metaTitle} description={post.metaDescription} path={path} jsonLd={jsonLd} />

      <div className="w-full overflow-x-clip bg-white font-sans text-[#28194f] antialiased">
        {/* ===== ARTICLE HEADER ===== */}
        <header className="relative isolate w-full overflow-hidden bg-gradient-to-br from-[#fbf7ff] via-white to-[#effbf8] pb-12 pt-28 sm:pb-16 sm:pt-36">
          <div className="pointer-events-none absolute -right-24 -top-24 h-80 w-80 rounded-full bg-[#702cb0]/10 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-24 -left-24 h-80 w-80 rounded-full bg-[#12dfbd]/15 blur-3xl" />

          <div className="relative z-10 mx-auto max-w-3xl px-4 sm:px-6">
            <nav aria-label="Breadcrumb" className="mb-6 flex flex-wrap items-center gap-1.5 text-xs font-medium text-slate-500">
              <Link to="/" className="hover:text-[#48247f]">Home</Link>
              <span>/</span>
              <Link to="/blogs" className="hover:text-[#48247f]">Blogs</Link>
              <span>/</span>
              <span className="line-clamp-1 text-slate-700">{post.category}</span>
            </nav>

            <span className="inline-flex items-center gap-1.5 rounded-full bg-[#f1ecfa] px-3 py-1 text-xs font-semibold text-[#48247f]">
              <Tag className="h-3.5 w-3.5 text-[#702cb0]" />
              {post.category}
            </span>

            <h1 className="mt-4 text-balance text-[30px] font-normal leading-[1.2] tracking-tight text-[#28194f] sm:text-[40px]">
              {post.title}
            </h1>

            <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs font-medium text-slate-500">
              <span className="inline-flex items-center gap-1.5">
                <CalendarDays className="h-3.5 w-3.5" />
                <time dateTime={post.published}>{formatDate(post.published)}</time>
              </span>
              <span className="inline-flex items-center gap-1.5">
                <Clock className="h-3.5 w-3.5" />
                {post.readTime}
              </span>
              <span>By the LMSBOTS Team</span>
            </div>
          </div>
        </header>

        {/* ===== BODY ===== */}
        <div className="mx-auto grid w-full max-w-[1100px] gap-12 px-4 py-12 sm:px-6 lg:grid-cols-[minmax(0,1fr)_250px] lg:py-16">
          <article className="min-w-0 max-w-3xl">
            {/* Intro */}
            <div className="text-[17px]">
              <Paragraphs items={post.intro} />
            </div>

            {/* Mobile table of contents */}
            <details className="mb-10 rounded-xl border border-[#48247f]/10 bg-[#faf7ff] p-4 lg:hidden">
              <summary className="flex cursor-pointer items-center gap-2 text-sm font-semibold text-[#48247f]">
                <ListOrdered className="h-4 w-4" /> In this article
              </summary>
              <ol className="mt-3 space-y-2 text-sm">
                {post.sections.map((s) => (
                  <li key={s.heading}>
                    <a href={`#${slugify(s.heading)}`} className="text-slate-700 hover:text-[#702cb0]">{s.heading}</a>
                  </li>
                ))}
                <li><a href="#faq" className="text-slate-700 hover:text-[#702cb0]">Frequently asked questions</a></li>
              </ol>
            </details>

            {/* Sections */}
            {post.sections.map((s) => (
              <section key={s.heading} id={slugify(s.heading)} className="scroll-mt-28">
                <h2 className="mb-4 mt-12 text-[24px] font-medium leading-snug tracking-tight text-[#28194f] sm:text-[28px]">
                  {s.heading}
                </h2>
                <Paragraphs items={s.paragraphs} />
                <Bullets items={s.bullets} />
                {(s.subsections || []).map((sub) => (
                  <div key={sub.heading}>
                    <h3 className="mb-3 mt-8 text-[19px] font-semibold leading-snug text-[#3b2a63] sm:text-[21px]">
                      {sub.heading}
                    </h3>
                    <Paragraphs items={sub.paragraphs} />
                    <Bullets items={sub.bullets} />
                  </div>
                ))}
              </section>
            ))}

            {/* FAQ */}
            <section id="faq" className="scroll-mt-28">
              <h2 className="mb-5 mt-14 text-[24px] font-medium tracking-tight text-[#28194f] sm:text-[28px]">
                Frequently asked questions
              </h2>
              <div className="space-y-3">
                {post.faq.map((f) => (
                  <details key={f.q} className="group rounded-xl border border-[#48247f]/10 bg-white p-4 shadow-sm open:border-[#702cb0]/30 open:bg-[#fcfaff]">
                    <summary className="flex cursor-pointer list-none items-start justify-between gap-4 text-[16px] font-semibold text-[#28194f]">
                      {f.q}
                      <span className="mt-0.5 shrink-0 text-[#702cb0] transition-transform duration-200 group-open:rotate-45">+</span>
                    </summary>
                    <p className="mt-3 text-[15px] leading-[1.8] text-slate-700">{renderInline(f.a)}</p>
                  </details>
                ))}
              </div>
            </section>

            {/* Conclusion */}
            <section className="mt-14">
              <h2 className="mb-4 text-[24px] font-medium tracking-tight text-[#28194f] sm:text-[28px]">Final thoughts</h2>
              <Paragraphs items={post.conclusion} />
            </section>

            {/* CTA */}
<div className="mt-12 overflow-hidden rounded-2xl bg-gradient-to-br from-[#28194f] via-[#48247f] to-[#702cb0] p-7 text-white sm:p-9">
              <h3 className="text-xl font-medium sm:text-2xl">See how LMSBOTS handles this for your team</h3>
              <p className="mt-2 max-w-xl text-sm leading-relaxed text-white/80">
                Capture leads, assign them instantly, follow up over calls and WhatsApp, and track every
                conversation in one place.
              </p>
              <div className="mt-5 flex flex-wrap gap-3">
                <Link to="/register" className="inline-flex items-center gap-1.5 rounded-lg bg-[#12dfbd] px-4 py-2.5 text-sm font-bold text-[#102b37] transition-colors hover:bg-[#40f0d3]">
                  Get started <ArrowRight className="h-4 w-4" />
                </Link>
                <Link to="/contact" className="inline-flex items-center rounded-lg border border-white/30 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-white/10">
                  Talk to us
                </Link>
              </div>
            </div>
          </article>

          {/* Desktop sticky table of contents */}
          <aside className="hidden lg:block">
            <div className="sticky top-28 rounded-xl border border-[#48247f]/10 bg-[#faf7ff] p-5">
              <p className="mb-3 flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-[#48247f]">
                <ListOrdered className="h-4 w-4" /> In this article
              </p>
              <ol className="max-h-[60vh] space-y-2.5 overflow-y-auto pr-1 text-[13px] leading-snug">
                {post.sections.map((s) => (
                  <li key={s.heading}>
                    <a href={`#${slugify(s.heading)}`} className="text-slate-600 transition-colors hover:text-[#702cb0]">
                      {s.heading}
                    </a>
                  </li>
                ))}
                <li><a href="#faq" className="text-slate-600 transition-colors hover:text-[#702cb0]">Frequently asked questions</a></li>
              </ol>
            </div>
          </aside>
        </div>

        {/* ===== RELATED ===== */}
        <section className="border-t border-[#48247f]/10 bg-[#faf7ff] py-14">
          <div className="mx-auto max-w-[1100px] px-4 sm:px-6">
            <h2 className="mb-8 text-2xl font-medium tracking-tight text-[#28194f]">Keep reading</h2>
            <div className="grid gap-6 md:grid-cols-3">
              {related.map((r) => (
                <Link
                  key={r.id}
                  to={`/blogs/${r.id}`}
                  className="group flex flex-col rounded-2xl border border-[#48247f]/10 bg-white p-6 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-[#702cb0]/40 hover:shadow-lg"
                >
                  <span className="mb-3 inline-flex w-fit items-center gap-1.5 rounded-full bg-[#f1ecfa] px-2.5 py-1 text-[11px] font-semibold text-[#48247f]">
                    <Tag className="h-3 w-3 text-[#702cb0]" /> {r.category}
                  </span>
                  <h3 className="text-[17px] font-medium leading-snug text-[#28194f] group-hover:text-[#48247f]">{r.title}</h3>
                  <span className="mt-auto inline-flex items-center gap-1 pt-4 text-xs font-semibold text-[#702cb0]">
                    Read article <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default BlogPost;













